import { connect } from 'react-redux';
import get from 'lodash.get';
import { createSelector } from 'reselect';

import Global from '../../../../../store/global-selectors';
import * as ShipmentConfigActions from '../../../../../store/csw/csw.actions';
import { injectIntl } from 'react-intl';
import { getVisiblePackingSlips } from '../../../../../hooks/use-visible-packing-slips';
import { OrderDetailsHelperService } from '../../../../../services/order-details-helper-service';
import OrderTemplates from './index';
import messages from './messages';

const getDontSendShipmentNotificationTemplate = createSelector(
  (intl) => intl,
  (intl) => ({
    emailTemplateId: null,
    name: intl.formatMessage(messages.dontSendShipmentNotification),
  }),
);

const getDontSendDeliveryNotificationTemplate = createSelector(
  (intl) => intl,
  (intl) => ({
    emailTemplateId: null,
    name: intl.formatMessage(messages.dontSendDeliveryNotification),
  }),
);

const mapStatesToProps = (state, { fulfillmentPlanId, intl, isActive, salesOrderId }) => {
  const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
    fulfillmentPlanId,
  });
  const {
    deliveryEmailNotificationTemplateId: entityDeliveryEmailNotificationTemplateId,
    shipmentEmailNotificationTemplateId: entityShipmentEmailNotificationTemplateId,
  } = fulfillmentPlan;

  const orderStoreId = Global.selectEntitiesSalesOrderProperty(state, {
    salesOrderId,
    property: 'store.storeGuid',
  });
  const selectedOrderStoreData = Global.selectSellerStoreData(state, {
    storeGuid: orderStoreId,
  });

  /* Packing Slips */
  const sellerPackingSlipFormat = Global.selectSellerSellerSetting(state, {
    settingName: 'PSFormat',
  });
  const sellerPackingSlips = getVisiblePackingSlips(state);
  const packingSlips = sellerPackingSlips.filter((slip) => sellerPackingSlipFormat && sellerPackingSlipFormat.includes(slip.size));
  const packingSlipIdToDisplay = Global.selectEntitiesFulfillmentPlanPackingSlipTemplateId(state, {
    fulfillmentPlanId,
  });
  const selectedPackingSlip = OrderDetailsHelperService.determineDefaultPackingSlipFormat(
    selectedOrderStoreData,
    sellerPackingSlipFormat,
    packingSlips,
    packingSlipIdToDisplay,
  );

  /* Shipment Notification Templates */
  const dontSendShipmentNotificationTemplate = getDontSendShipmentNotificationTemplate(intl);
  let shipmentTemplates = Global.selectSellerShipmentTemplates(state) || [];
  if (!shipmentTemplates.includes(dontSendShipmentNotificationTemplate)) {
    shipmentTemplates = [...shipmentTemplates, dontSendShipmentNotificationTemplate];
  }
  // Determine selected option
  const cswShipmentTemplateId = Global.selectCSWShipmentTemplateId(state, {
    fulfillmentPlanId,
  });
  const shipmentTemplateIdToDisplay = isActive ? cswShipmentTemplateId : entityShipmentEmailNotificationTemplateId;
  const selectedShipmentTemplate =
    OrderDetailsHelperService.determineDefaultShipmentTemplate(selectedOrderStoreData, shipmentTemplates, shipmentTemplateIdToDisplay) ||
    dontSendShipmentNotificationTemplate;

  /* Delivery Notification Templates */
  const dontSendDeliveryNotificationTemplate = getDontSendDeliveryNotificationTemplate(intl);
  let deliveryTemplates = Global.selectSellerDeliveryTemplates(state);
  if (!deliveryTemplates.includes(dontSendDeliveryNotificationTemplate)) {
    deliveryTemplates = [...deliveryTemplates, dontSendDeliveryNotificationTemplate];
  }
  // Determine selected option
  const cswDeliveryTemplateId = Global.selectCSWDeliveryTemplateId(state, {
    fulfillmentPlanId,
  });
  const deliveryTemplateIdToDisplay = isActive ? cswDeliveryTemplateId : entityDeliveryEmailNotificationTemplateId;
  const selectedDeliveryTemplate =
    OrderDetailsHelperService.determineDefaultDeliveryTemplate(selectedOrderStoreData, deliveryTemplates, deliveryTemplateIdToDisplay) ||
    dontSendDeliveryNotificationTemplate;

  return {
    fulfillmentPlanId,
    salesOrderId,
    packingSlips,
    shipmentTemplates,
    deliveryTemplates,
    selectedPackingSlip,
    selectedShipmentTemplate,
    selectedDeliveryTemplate,
  };
};

const mapDispatchToProps = (dispatch, { fulfillmentPlanId }) => {
  return {
    onPackingSlipSelected: (packingSlip) => {
      const packingSlipTemplateId = get(packingSlip, 'packingSlipId');
      dispatch(
        ShipmentConfigActions.updateShipmentConfiguration(fulfillmentPlanId, {
          packingSlipTemplateId,
        }),
      );
      dispatch(
        ShipmentConfigActions.saveConfigWithPayload(fulfillmentPlanId, {
          packingSlipTemplateId,
        }),
      );
    },
    onShipmentNotificationSelected: (template) => {
      const shipmentEmailNotificationTemplateId = get(template, 'emailTemplateId');
      dispatch(
        ShipmentConfigActions.updateShipmentConfiguration(fulfillmentPlanId, {
          shipmentEmailNotificationTemplateId,
        }),
      );
      dispatch(
        ShipmentConfigActions.saveConfigWithPayload(fulfillmentPlanId, {
          shipmentEmailNotificationTemplateId,
        }),
      );
    },
    onDeliveryNotificationSelected: (template) => {
      const deliveryEmailNotificationTemplateId = get(template, 'emailTemplateId');
      dispatch(
        ShipmentConfigActions.updateShipmentConfiguration(fulfillmentPlanId, {
          deliveryEmailNotificationTemplateId,
        }),
      );
      dispatch(
        ShipmentConfigActions.saveConfigWithPayload(fulfillmentPlanId, {
          deliveryEmailNotificationTemplateId,
        }),
      );
    },
  };
};

export default injectIntl(connect(mapStatesToProps, mapDispatchToProps)(OrderTemplates));
